function hasShellPortArg(command: string): boolean {
  return /(?:^|\s)(?:--port(?:=|\s)|-p(?:=|\s))/u.test(command);
}

function hasShellHostArg(command: string): boolean {
  return /(?:^|\s)(?:--host(?:=|\s)|--hostname(?:=|\s)|-H(?:=|\s))/u.test(command);
}

function usesPackageDevScript(command: string): boolean {
  return /\b(?:bun|npm|pnpm|yarn)\s+(?:run\s+)?dev\b/u.test(command);
}

function needsPackageDevScriptSeparator(command: string): boolean {
  return /\b(?:bun|npm|pnpm)\s+(?:run\s+)?dev\b/u.test(command) && !/\s--(?:\s|$)/u.test(command);
}

function appendDevServerArgs(command: string, args: readonly string[]): string {
  if (args.length === 0) {
    return command;
  }
  const separator = needsPackageDevScriptSeparator(command) ? " --" : "";
  return `${command}${separator} ${args.join(" ")}`;
}

function buildNextDevServerArgs(
  command: string,
  allocatedPort: number,
  host: string,
): readonly string[] {
  return [
    ...(hasShellHostArg(command) ? [] : ["--hostname", host]),
    ...(hasShellPortArg(command) ? [] : ["--port", String(allocatedPort)]),
  ];
}

function buildCommonDevServerArgs(
  command: string,
  allocatedPort: number,
  host: string,
): readonly string[] {
  return [
    ...(hasShellHostArg(command) ? [] : ["--host", host]),
    ...(hasShellPortArg(command) ? [] : ["--port", String(allocatedPort)]),
  ];
}

function hasStrictPortArg(command: string): boolean {
  return /(?:^|\s)--strictPort(?:=|\s|$)/u.test(command);
}

function buildViteDevServerArgs(
  command: string,
  allocatedPort: number,
  host: string,
): readonly string[] {
  return [
    ...buildCommonDevServerArgs(command, allocatedPort, host),
    ...(hasStrictPortArg(command) ? [] : ["--strictPort"]),
  ];
}

function dedupeShellCommands(commands: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const command of commands) {
    const trimmed = command.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    unique.push(trimmed);
  }
  return unique;
}

function joinShellFallbacks(commands: readonly string[]): string {
  const uniqueCommands = dedupeShellCommands(commands);
  if (uniqueCommands.length === 0) {
    return "false";
  }
  if (uniqueCommands.length === 1) {
    return uniqueCommands[0] ?? "false";
  }
  return `(${uniqueCommands.join(" || ")})`;
}

function withDevServerEnv(command: string, allocatedPort: number, host: string): string {
  return `PORT=${allocatedPort} HOST=${host} ${command}`;
}

function buildPackageDevScriptCommand(
  command: string,
  allocatedPort: number,
  host: string,
): string {
  const commonCommand = appendDevServerArgs(
    command,
    buildCommonDevServerArgs(command, allocatedPort, host),
  );
  const nextCommand = appendDevServerArgs(
    command,
    buildNextDevServerArgs(command, allocatedPort, host),
  );
  return joinShellFallbacks([
    commonCommand,
    nextCommand,
    withDevServerEnv(command, allocatedPort, host),
  ]);
}

function buildHeuristicPackageDevScriptCommand(allocatedPort: number, host: string): string {
  const commands = ["bun run dev", "npm run dev", "pnpm dev", "yarn dev"];
  const attempts = commands.flatMap((command) => [
    appendDevServerArgs(command, buildCommonDevServerArgs(command, allocatedPort, host)),
    appendDevServerArgs(command, buildNextDevServerArgs(command, allocatedPort, host)),
    withDevServerEnv(command, allocatedPort, host),
  ]);
  return joinShellFallbacks(attempts);
}

export function buildPortAwareDevServerCommand(input: {
  readonly command: string;
  readonly allocatedPort: number;
  readonly source: "project-script" | "heuristic";
  /**
   * Interface the dev server binds to. Defaults to "0.0.0.0" to preserve the
   * existing human "Launch app" behavior. Pass "127.0.0.1" for agent-started
   * servers so they are not reachable from outside the sprite/VM.
   */
  readonly host?: string;
}): string {
  const host = input.host ?? "0.0.0.0";
  const port = String(input.allocatedPort);
  if (input.source === "heuristic") {
    return `if [ -f "package.json" ]; then ${buildHeuristicPackageDevScriptCommand(input.allocatedPort, host)}; elif [ -f "deno.json" ] || [ -f "deno.jsonc" ]; then PORT=${port} HOST=${host} deno task dev; else echo "No dev command detected for this project." >&2; fi`;
  }

  const command = input.command.trim();
  const lowerCommand = command.toLowerCase();
  if (usesPackageDevScript(command)) {
    return buildPackageDevScriptCommand(command, input.allocatedPort, host);
  }
  if (/\bnext\b/u.test(lowerCommand)) {
    return appendDevServerArgs(command, buildNextDevServerArgs(command, input.allocatedPort, host));
  }
  if (/\b(vite|astro|nuxt|svelte-kit|remix)\b|webpack\s+serve/u.test(lowerCommand)) {
    return appendDevServerArgs(command, buildViteDevServerArgs(command, input.allocatedPort, host));
  }
  return `PORT=${port} HOST=${host} ${command}`;
}
